import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Building2, Users, UserCheck, Clock, Mail, Loader2, Plus, Search,
  LogOut, ArrowRight, X, Briefcase, Landmark,
} from "lucide-react";
import SEO from "@/components/SEO";
import { api, useAuth, formatApiError } from "@/auth/AuthContext";

export const TYPE_LABELS = { travel_partner: "Travel Partner", corporate_client: "Corporate Client" };
export const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
  inactive: "bg-gray-100 text-gray-600 border-gray-200",
  invited: "bg-blue-50 text-blue-700 border-blue-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-gray-100 text-gray-600 border-gray-200",
  revoked: "bg-red-50 text-red-700 border-red-200",
};

export const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border ${STATUS_STYLES[status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
    {status}
  </span>
);

export const AdminShell = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]"><Loader2 className="animate-spin text-[#C62828]" size={28}/></div>;
  }
  if (user === null) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "super_admin") return <Navigate to="/portal" replace />;

  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]" data-testid="admin-platform">
      <SEO title={`${title} — Admin — The China Explorer`} description="The China Explorer Admin Platform." path="/admin" />
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/admin" className="font-heading text-lg tracking-tight">
            The China Explorer <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] ml-2">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-[11px] text-white/60">{user.email}</span>
            <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-signout"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-semibold border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2 transition-colors">
              <LogOut size={12}/> Sign Out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">{children}</main>
    </div>
  );
};

const MetricCard = ({ label, value, icon: Icon, testId }) => (
  <div className="bg-white border border-[#EAE6DF] p-6" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">{label}</p>
      <Icon size={16} className="text-[#0E2A5C]" strokeWidth={1.5}/>
    </div>
    <p className="font-heading text-4xl text-[#0E2A5C] mt-3">{value}</p>
  </div>
);

const COUNTRIES = ["United Arab Emirates","Saudi Arabia","Qatar","Kuwait","Bahrain","Oman","China","United Kingdom","United States","France","Germany","Spain","Russia","India","Egypt","Jordan","Other"];

const Input = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">{label}{required && " *"}</label>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 w-full px-3.5 py-2.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 w-full px-3.5 py-2.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const EMPTY_ORG = {
  name: "", legal_name: "", account_type: "travel_partner", country: "United Arab Emirates",
  city: "", address: "", contact_name: "", contact_email: "", contact_phone: "",
  website: "", registration_number: "", preferred_currency: "USD", status: "active", internal_notes: "",
};

const AddOrgModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY_ORG);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/admin/organizations", form);
      onCreated(data.organization);
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 md:p-10" data-testid="add-org-modal">
      <form onSubmit={submit} className="bg-white border border-[#EAE6DF] w-full max-w-2xl p-8 md:p-10 relative">
        <button type="button" onClick={onClose} data-testid="add-org-close" className="absolute top-4 right-4 text-[#4A4A4A] hover:text-[#C62828]"><X size={18}/></button>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ New B2B Account</p>
        <h2 className="font-heading text-2xl mt-2 text-[#0E2A5C]">Add Organization</h2>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Input label="Organization Name" value={form.name} onChange={set("name")} required testId="org-name"/>
          <Input label="Legal Company Name" value={form.legal_name} onChange={set("legal_name")} required testId="org-legal-name"/>
          <Select label="Account Type" value={form.account_type} onChange={set("account_type")} testId="org-account-type"
            options={[{ value: "travel_partner", label: "Travel Partner (Agency / TMC)" }, { value: "corporate_client", label: "Corporate Client (Company / SME)" }]}/>
          <Select label="Account Status" value={form.status} onChange={set("status")} testId="org-status"
            options={["pending","active","suspended","inactive"].map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))}/>
          <Select label="Country" value={form.country} onChange={set("country")} testId="org-country"
            options={COUNTRIES.map((c) => ({ value: c, label: c }))}/>
          <Input label="City" value={form.city} onChange={set("city")} required testId="org-city"/>
          <div className="sm:col-span-2"><Input label="Address" value={form.address} onChange={set("address")} testId="org-address"/></div>
          <Input label="Main Contact Name" value={form.contact_name} onChange={set("contact_name")} required testId="org-contact-name"/>
          <Input label="Main Contact Email" type="email" value={form.contact_email} onChange={set("contact_email")} required testId="org-contact-email"/>
          <Input label="Main Contact Phone" value={form.contact_phone} onChange={set("contact_phone")} required testId="org-contact-phone"/>
          <Input label="Website (optional)" value={form.website} onChange={set("website")} testId="org-website"/>
          <Input label="Trade License / Reg. No. (optional)" value={form.registration_number} onChange={set("registration_number")} testId="org-regno"/>
          <Select label="Preferred Currency" value={form.preferred_currency} onChange={set("preferred_currency")} testId="org-currency"
            options={["USD","AED","EUR","GBP","CNY","SAR"].map((c) => ({ value: c, label: c }))}/>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">Internal Notes (admin only)</label>
            <textarea value={form.internal_notes} onChange={(e) => set("internal_notes")(e.target.value)} rows={3} data-testid="org-notes"
              className="mt-1 w-full px-3.5 py-2.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-[#C62828]" data-testid="add-org-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="add-org-submit"
          className="mt-6 w-full h-[48px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 transition-colors">
          {loading ? <Loader2 size={15} className="animate-spin"/> : <>Create Organization <ArrowRight size={13}/></>}
        </button>
      </form>
    </div>
  );
};

export default function AdminPlatform() {
  const [metrics, setMetrics] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (typeFilter) params.account_type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    const [dash, orgRes] = await Promise.all([
      api.get("/admin/dashboard"),
      api.get("/admin/organizations", { params }),
    ]);
    setMetrics(dash.data.metrics);
    setOrgs(orgRes.data.organizations);
  }, [search, typeFilter, statusFilter]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  return (
    <AdminShell title="Dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ B2B Admin Platform</p>
          <h1 className="font-heading text-4xl mt-2 text-[#0E2A5C]">Dashboard</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-org-button"
          className="inline-flex items-center gap-2 bg-[#C62828] hover:bg-[#B71C1C] text-white px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
          <Plus size={14}/> Add Organization
        </button>
      </div>
      <div className="mt-4">
        <Link to="/admin/commerce" data-testid="admin-commerce-link"
          className="inline-flex items-center gap-2 border border-[#0E2A5C] text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
          Journeys & Bookings →
        </Link>
      </div>

      {/* Real metrics */}
      {metrics ? (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-metrics">
          <MetricCard label="Total Organizations" value={metrics.total_organizations} icon={Building2} testId="metric-total-orgs"/>
          <MetricCard label="Travel Partners" value={metrics.travel_partners} icon={Briefcase} testId="metric-travel-partners"/>
          <MetricCard label="Corporate Clients" value={metrics.corporate_clients} icon={Landmark} testId="metric-corporate-clients"/>
          <MetricCard label="Active Accounts" value={metrics.active_accounts} icon={UserCheck} testId="metric-active-accounts"/>
          <MetricCard label="Pending Accounts" value={metrics.pending_accounts} icon={Clock} testId="metric-pending-accounts"/>
          <MetricCard label="Total Users" value={metrics.total_users} icon={Users} testId="metric-total-users"/>
          <MetricCard label="Active Users" value={metrics.active_users} icon={UserCheck} testId="metric-active-users"/>
          <MetricCard label="Pending Invitations" value={metrics.pending_invitations} icon={Mail} testId="metric-pending-invitations"/>
        </div>
      ) : (
        <div className="mt-8 flex justify-center py-10"><Loader2 className="animate-spin text-[#C62828]" size={24}/></div>
      )}

      {/* Future-ready */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-future-metrics">
        {["Flight Bookings","Sales","Revenue","API Activity","Payments","Outstanding Balances"].map((l) => (
          <div key={l} className="border border-dashed border-[#EAE6DF] bg-white/50 p-4">
            <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#4A4A4A]">{l}</p>
            <p className="font-heading text-xl text-[#4A4A4A]/50 mt-1">—</p>
            <p className="text-[8px] uppercase tracking-[0.15em] text-[#D4AF37] font-bold mt-1">Coming soon</p>
          </div>
        ))}
      </div>

      {/* Organizations */}
      <section className="mt-12">
        <h2 className="font-heading text-2xl text-[#0E2A5C]">Organizations</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, legal name or email…" data-testid="org-search"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="org-type-filter"
            className="px-3 py-2.5 bg-white border border-[#EAE6DF] text-sm focus:outline-none">
            <option value="">All types</option>
            <option value="travel_partner">Travel Partner</option>
            <option value="corporate_client">Corporate Client</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="org-status-filter"
            className="px-3 py-2.5 bg-white border border-[#EAE6DF] text-sm focus:outline-none">
            <option value="">All statuses</option>
            {["pending","active","suspended","inactive"].map((s) => <option key={s} value={s}>{s[0].toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>

        <div className="mt-4 bg-white border border-[#EAE6DF] overflow-x-auto">
          {orgs === null ? (
            <div className="p-10 text-center"><Loader2 className="animate-spin text-[#C62828] mx-auto" size={22}/></div>
          ) : orgs.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#4A4A4A]" data-testid="orgs-empty">
              No organizations yet. Click <strong>+ Add Organization</strong> to onboard your first B2B account.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="orgs-table">
              <thead>
                <tr className="border-b border-[#EAE6DF] text-left">
                  {["Organization","Type","Country","Contact","Users","Status",""].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4A4A4A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} className="border-b border-[#EAE6DF]/60 last:border-0 hover:bg-[#F7F5F0] cursor-pointer"
                    onClick={() => navigate(`/admin/organizations/${o.id}`)} data-testid={`org-row-${o.id}`}>
                    <td className="px-4 py-3.5 font-semibold text-[#0E2A5C]">{o.name}</td>
                    <td className="px-4 py-3.5">{TYPE_LABELS[o.account_type]}</td>
                    <td className="px-4 py-3.5">{o.country}</td>
                    <td className="px-4 py-3.5 text-[#4A4A4A]">{o.contact_email}</td>
                    <td className="px-4 py-3.5">{o.user_count}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={o.status}/></td>
                    <td className="px-4 py-3.5 text-right"><ArrowRight size={14} className="text-[#4A4A4A]"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {showAdd && (
        <AddOrgModal
          onClose={() => setShowAdd(false)}
          onCreated={(org) => { setShowAdd(false); navigate(`/admin/organizations/${org.id}`); }}
        />
      )}
    </AdminShell>
  );
}
